/* React */
import type { RefObject } from "react";

/* d3.js */
import * as d3 from "d3";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Utils */
import {
  computePlotHeightWidth,
  createViewBox,
} from "src/utils/helper/charts/helper";

import {
  drawAxes,
  createBarchartCategories,
  computeBarchartStackData,
  drawBarsWithRoundedCorners,
  createGradients,
} from "src/components/charts/stacked-barchart/helper";

/* Interfaces */
import { BarchartProps } from "src/shared/interfaces/charts/Barchart";
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";

export const createStackedBarchart = (
  svg: SVGSelection,
  tooltipRef: RefObject<ChartTooltipRef>,
  data: BarchartProps[],
  dimensions: ChartDimensionsProps,
  unit?: string
) => {
  const { margin, height, width } = computePlotHeightWidth(
    dimensions,
    {
      top: 62,
      right: 92,
      bottom: 32,
      left: 92,
    }
  );

  createViewBox(svg, dimensions);

  const subgroups = data.map((d) => d.name);

  const { categories, xScale } = createBarchartCategories(
    data,
    width
  );

  const formattedDataForStack = computeBarchartStackData(
    data,
    categories,
    subgroups
  );

  const stack = d3
    .stack<Record<string, number>>()
    .keys(subgroups)(formattedDataForStack);

  const maxStackValue =
    d3.max(formattedDataForStack, (d) =>
      subgroups.reduce((sum, key) => sum + (d[key] ?? 0), 0)
    ) ?? 0;

  const yScale = d3
    .scaleLinear()
    .domain([0, maxStackValue])
    .nice()
    .range([height, 0]);

  const g = svg
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top})`
    );

  drawAxes(g, xScale, yScale, height, width, unit);
  createGradients(svg, subgroups);

  drawBarsWithRoundedCorners(
    svg,
    g,
    stack,
    subgroups,
    categories,
    xScale,
    yScale,
    tooltipRef
  );
};
